import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  Circle,
  Clipboard,
  FileCheck2,
  Loader2,
  OctagonAlert,
  PlayCircle,
  XCircle,
} from 'lucide-react';
import {
  completeLiveDeployment,
  fetchLiveDeployment,
  recordLiveDeploymentEvidence,
  recordLiveDeploymentStepAttempt,
  type LiveDeploymentAttemptInput,
  type LiveDeploymentEvidence,
  type LiveDeploymentSnapshot,
  type LiveDeploymentStep,
  type LiveDeploymentStepStatus,
} from '@/lib/api';
import { queryClient } from '@/lib/query-client';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

export type { LiveDeploymentSnapshot };

const AARON_OUTCOME_ID = 'O-2026-06-17-hybrid-mos-maestro-enrollment-closeout';

const STATUS_OPTIONS: LiveDeploymentStepStatus[] = [
  'pending',
  'running',
  'passed',
  'failed',
  'blocked',
  'skipped',
];

interface AttemptFormState {
  status: LiveDeploymentStepStatus;
  notes: string;
  output: string;
  failureReason: string;
  nextAction: string;
}

type PendingAction = 'attempt' | 'evidence' | 'complete' | null;

export interface AaronLiveDeploymentViewProps {
  snapshot: LiveDeploymentSnapshot;
  activeStepId: string;
  activeEvidenceId: string;
  attemptForm: AttemptFormState;
  evidenceContent: string;
  message: string | null;
  error: string | null;
  pendingAction: PendingAction;
  onActiveStepChange: (stepId: string) => void;
  onActiveEvidenceChange: (evidenceId: string) => void;
  onAttemptFormChange: (field: keyof AttemptFormState, value: string) => void;
  onEvidenceContentChange: (value: string) => void;
  onRecordAttempt: () => void;
  onRecordEvidence: () => void;
  onComplete: () => void;
}

function statusLabel(status: LiveDeploymentStepStatus): string {
  return status.replace(/_/g, ' ');
}

function StatusIcon({ status }: { status: LiveDeploymentStepStatus }): React.ReactElement {
  if (status === 'passed') return <CheckCircle2 className="h-4 w-4 text-success" />;
  if (status === 'failed') return <XCircle className="h-4 w-4 text-error" />;
  if (status === 'running') return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
  if (status === 'blocked') return <OctagonAlert className="h-4 w-4 text-warning" />;
  return <Circle className="h-4 w-4 text-text-tertiary" />;
}

function statusBadgeClass(status: LiveDeploymentStepStatus): string {
  if (status === 'passed') return 'border-success/40 text-success';
  if (status === 'failed') return 'border-error/40 text-error';
  if (status === 'blocked') return 'border-warning/40 text-warning';
  if (status === 'running') return 'border-primary/40 text-primary';
  return 'border-border text-text-secondary';
}

function evidenceStatusLabel(evidence: LiveDeploymentEvidence): string {
  if (!evidence.exists) return 'missing';
  if (evidence.template) return 'template';
  return evidence.valid ? 'valid' : 'invalid';
}

function latestAttempt(step: LiveDeploymentStep): string | null {
  const attempt = step.attempts.at(-1);
  if (!attempt) return null;
  const detail = attempt.failureReason ?? attempt.notes ?? attempt.nextAction;
  return detail ? `${attempt.status}: ${detail}` : attempt.status;
}

function attemptInput(form: AttemptFormState): LiveDeploymentAttemptInput {
  return {
    status: form.status,
    ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    ...(form.output.trim() ? { output: form.output.trim() } : {}),
    ...(form.failureReason.trim() ? { failureReason: form.failureReason.trim() } : {}),
    ...(form.nextAction.trim() ? { nextAction: form.nextAction.trim() } : {}),
  };
}

function copyText(value: string | null): void {
  if (!value) return;
  void navigator.clipboard?.writeText(value);
}

export function AaronLiveDeploymentView({
  snapshot,
  activeStepId,
  activeEvidenceId,
  attemptForm,
  evidenceContent,
  message,
  error,
  pendingAction,
  onActiveStepChange,
  onActiveEvidenceChange,
  onAttemptFormChange,
  onEvidenceContentChange,
  onRecordAttempt,
  onRecordEvidence,
  onComplete,
}: AaronLiveDeploymentViewProps): React.ReactElement {
  const activeStep = snapshot.steps.find(step => step.id === activeStepId) ?? snapshot.steps[0];
  const activeEvidence =
    snapshot.evidence.find(evidence => evidence.id === activeEvidenceId) ?? snapshot.evidence[0];

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      <header className="border-b border-border bg-surface px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-text-primary">Aaron Live Deployment</h1>
            <p className="mt-1 max-w-3xl text-sm text-text-secondary">{snapshot.outcomeId}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={snapshot.completionReady ? 'text-success' : ''}>
              {snapshot.completionReady ? 'ready to complete' : 'blocked'}
            </Badge>
            <Button
              type="button"
              onClick={onComplete}
              disabled={!snapshot.completionReady || pendingAction === 'complete'}
            >
              {pendingAction === 'complete' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileCheck2 className="h-4 w-4" />
              )}
              Complete outcome
            </Button>
          </div>
        </div>
        {message && <p className="mt-3 text-sm text-success">{message}</p>}
        {error && <p className="mt-3 text-sm text-error">{error}</p>}
      </header>

      <main className="grid min-h-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[minmax(360px,0.9fr)_minmax(480px,1.1fr)]">
        <section className="min-h-0 overflow-y-auto border-r border-border p-4">
          <div className="space-y-3">
            {snapshot.steps.map(step => (
              <button
                type="button"
                key={step.id}
                onClick={() => {
                  onActiveStepChange(step.id);
                }}
                className={cn(
                  'w-full rounded-md border border-border bg-surface p-3 text-left transition-colors hover:border-primary/50',
                  activeStep?.id === step.id && 'border-primary bg-surface-elevated'
                )}
              >
                <div className="flex items-start gap-3">
                  <StatusIcon status={step.status} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-sm font-semibold text-text-primary">{step.label}</h2>
                      <Badge variant="outline" className={statusBadgeClass(step.status)}>
                        {statusLabel(step.status)}
                      </Badge>
                    </div>
                    <p className="mt-1 text-xs text-text-secondary">{step.description}</p>
                    {latestAttempt(step) && (
                      <p className="mt-2 line-clamp-2 text-xs text-text-tertiary">
                        {latestAttempt(step)}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="min-h-0 overflow-y-auto p-4">
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(280px,0.72fr)]">
            <div className="space-y-4">
              {activeStep && (
                <div className="rounded-md border border-border bg-surface p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="text-base font-semibold text-text-primary">
                        {activeStep.label}
                      </h2>
                      <p className="mt-1 text-sm text-text-secondary">{activeStep.description}</p>
                    </div>
                    <Badge variant="outline" className={statusBadgeClass(activeStep.status)}>
                      {statusLabel(activeStep.status)}
                    </Badge>
                  </div>

                  {activeStep.command && (
                    <div className="mt-4 rounded-md border border-border bg-background p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-xs font-medium uppercase text-text-tertiary">
                          Command
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            copyText(activeStep.command);
                          }}
                        >
                          <Clipboard className="h-4 w-4" />
                          Copy
                        </Button>
                      </div>
                      <pre className="whitespace-pre-wrap break-words text-xs text-text-primary">
                        {activeStep.command}
                      </pre>
                    </div>
                  )}

                  <div className="mt-4 grid gap-3">
                    <label className="grid gap-1 text-sm text-text-secondary">
                      Status
                      <select
                        value={attemptForm.status}
                        onChange={event => {
                          onAttemptFormChange('status', event.target.value);
                        }}
                        className="h-10 rounded-md border border-border bg-background px-3 text-sm text-text-primary"
                      >
                        {STATUS_OPTIONS.map(status => (
                          <option key={status} value={status}>
                            {statusLabel(status)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="grid gap-1 text-sm text-text-secondary">
                      Notes
                      <Textarea
                        value={attemptForm.notes}
                        onChange={event => {
                          onAttemptFormChange('notes', event.target.value);
                        }}
                      />
                    </label>
                    <label className="grid gap-1 text-sm text-text-secondary">
                      Output
                      <Textarea
                        value={attemptForm.output}
                        onChange={event => {
                          onAttemptFormChange('output', event.target.value);
                        }}
                        className="min-h-28 font-mono text-xs"
                      />
                    </label>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="grid gap-1 text-sm text-text-secondary">
                        Failure reason
                        <Textarea
                          value={attemptForm.failureReason}
                          onChange={event => {
                            onAttemptFormChange('failureReason', event.target.value);
                          }}
                        />
                      </label>
                      <label className="grid gap-1 text-sm text-text-secondary">
                        Next action
                        <Textarea
                          value={attemptForm.nextAction}
                          onChange={event => {
                            onAttemptFormChange('nextAction', event.target.value);
                          }}
                        />
                      </label>
                    </div>
                    <Button
                      type="button"
                      onClick={onRecordAttempt}
                      disabled={pendingAction === 'attempt'}
                    >
                      {pendingAction === 'attempt' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <PlayCircle className="h-4 w-4" />
                      )}
                      Record attempt
                    </Button>
                  </div>
                </div>
              )}

              <div className="rounded-md border border-border bg-surface p-4">
                <h2 className="text-base font-semibold text-text-primary">Evidence Capture</h2>
                <div className="mt-3 grid gap-3">
                  <label className="grid gap-1 text-sm text-text-secondary">
                    Evidence file
                    <select
                      value={activeEvidenceId}
                      onChange={event => {
                        onActiveEvidenceChange(event.target.value);
                      }}
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm text-text-primary"
                    >
                      {snapshot.evidence.map(evidence => (
                        <option key={evidence.id} value={evidence.id}>
                          {evidence.label} - {evidence.file}
                        </option>
                      ))}
                    </select>
                  </label>
                  {activeEvidence && (
                    <p className="text-xs text-text-tertiary">
                      Current status: {evidenceStatusLabel(activeEvidence)} -{' '}
                      {activeEvidence.summary}
                    </p>
                  )}
                  <Textarea
                    value={evidenceContent}
                    onChange={event => {
                      onEvidenceContentChange(event.target.value);
                    }}
                    className="min-h-44 font-mono text-xs"
                    placeholder="Paste redacted command output or session binding JSON"
                  />
                  <Button
                    type="button"
                    onClick={onRecordEvidence}
                    disabled={!activeEvidence || pendingAction === 'evidence'}
                  >
                    {pendingAction === 'evidence' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileCheck2 className="h-4 w-4" />
                    )}
                    Save evidence
                  </Button>
                </div>
              </div>
            </div>

            <aside className="space-y-4">
              <div className="rounded-md border border-border bg-surface p-4">
                <h2 className="text-sm font-semibold text-text-primary">Completion Blockers</h2>
                {snapshot.blockers.length === 0 ? (
                  <p className="mt-2 text-sm text-success">All gates are satisfied.</p>
                ) : (
                  <ul className="mt-2 space-y-2">
                    {snapshot.blockers.map(blocker => (
                      <li key={blocker} className="text-sm text-text-secondary">
                        {blocker}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-md border border-border bg-surface p-4">
                <h2 className="text-sm font-semibold text-text-primary">Evidence Files</h2>
                <div className="mt-3 space-y-2">
                  {snapshot.evidence.map(evidence => (
                    <button
                      type="button"
                      key={evidence.id}
                      onClick={() => {
                        onActiveEvidenceChange(evidence.id);
                      }}
                      className={cn(
                        'w-full rounded-md border border-border bg-background p-2 text-left text-sm',
                        activeEvidenceId === evidence.id && 'border-primary'
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-text-primary">{evidence.label}</span>
                        <Badge
                          variant="outline"
                          className={evidence.valid ? 'text-success' : 'text-warning'}
                        >
                          {evidenceStatusLabel(evidence)}
                        </Badge>
                      </div>
                      <p className="mt-1 break-words text-xs text-text-tertiary">{evidence.file}</p>
                    </button>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
    </div>
  );
}

export function AaronLiveDeploymentPage(): React.ReactElement {
  const [activeStepId, setActiveStepId] = useState('gitea-registry-auth-confirmed');
  const [activeEvidenceId, setActiveEvidenceId] = useState('enrollment-token');
  const [attemptForm, setAttemptForm] = useState<AttemptFormState>({
    status: 'pending',
    notes: '',
    output: '',
    failureReason: '',
    nextAction: '',
  });
  const [evidenceContent, setEvidenceContent] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queryKey = useMemo(() => ['liveDeployment', AARON_OUTCOME_ID], []);
  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => fetchLiveDeployment(AARON_OUTCOME_ID),
  });

  const recordAttempt = useMutation({
    mutationFn: () =>
      recordLiveDeploymentStepAttempt(AARON_OUTCOME_ID, activeStepId, attemptInput(attemptForm)),
    onSuccess: async () => {
      setMessage('Attempt recorded.');
      setError(null);
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: err => {
      setMessage(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const recordEvidence = useMutation({
    mutationFn: () =>
      recordLiveDeploymentEvidence(AARON_OUTCOME_ID, activeEvidenceId, evidenceContent),
    onSuccess: async () => {
      setMessage('Evidence saved.');
      setError(null);
      setEvidenceContent('');
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: err => {
      setMessage(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const complete = useMutation({
    mutationFn: () => completeLiveDeployment(AARON_OUTCOME_ID),
    onSuccess: async () => {
      setMessage('Live deployment is ready for outcome closeout.');
      setError(null);
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: err => {
      setMessage(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-text-secondary">
        Loading Aaron live deployment...
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-error">
        Unable to load Aaron live deployment.
      </div>
    );
  }

  const pendingAction: PendingAction = recordAttempt.isPending
    ? 'attempt'
    : recordEvidence.isPending
      ? 'evidence'
      : complete.isPending
        ? 'complete'
        : null;

  return (
    <AaronLiveDeploymentView
      snapshot={data}
      activeStepId={activeStepId}
      activeEvidenceId={activeEvidenceId}
      attemptForm={attemptForm}
      evidenceContent={evidenceContent}
      message={message}
      error={error}
      pendingAction={pendingAction}
      onActiveStepChange={setActiveStepId}
      onActiveEvidenceChange={setActiveEvidenceId}
      onAttemptFormChange={(field, value) => {
        setAttemptForm(current => ({
          ...current,
          [field]:
            field === 'status' && STATUS_OPTIONS.includes(value as LiveDeploymentStepStatus)
              ? (value as LiveDeploymentStepStatus)
              : value,
        }));
      }}
      onEvidenceContentChange={setEvidenceContent}
      onRecordAttempt={() => {
        recordAttempt.mutate();
      }}
      onRecordEvidence={() => {
        recordEvidence.mutate();
      }}
      onComplete={() => {
        complete.mutate();
      }}
    />
  );
}
